// Swatches (DRAW_PLAN §7): the document's swatches. Click one to paint the active box (fill or stroke) of the
// selection. A global swatch is linked: editing it updates every object that uses it. New swatches come from
// the active box's colour; double-click a name to rename; the picker edits the chosen swatch.
import { useState } from 'react';
import { useT } from '../../../i18n';
import { useDesign } from '../../state';
import { useDesignUi } from '../designUi';
import { addSwatch, removeSwatch, updateSwatch } from '../../commands/commands';
import { setTargetColor, shownAppearance, targetColor } from '../look';
import { cssColor } from '../../render/paint';
import { ColorPicker } from '../ColorPicker';
import { BLACK } from '../../model/defaults';

export function SwatchesDock() {
  const t = useT();
  const doc = useDesign((st) => st.doc)!;
  const target = useDesignUi((st) => st.target);
  useDesignUi((st) => st.selection);
  const [chosen, setChosen] = useState<string | null>(null);
  const [global, setGlobal] = useState(true);
  const [rename, setRename] = useState<string | null>(null);
  const sw = doc.swatches.find((s) => s.id === chosen);
  return (
    <div data-testid="swatches-dock" style={{ display: 'grid', gap: 8 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        {doc.swatches.map((s) => (
          <button
            key={s.id}
            data-testid={`swatch-${s.name}`}
            title={`${s.name}${s.global ? ` (${t('globalSwatch')})` : ''}`}
            aria-label={s.name}
            aria-pressed={chosen === s.id}
            onClick={() => {
              setChosen(s.id);
              setTargetColor(s.global ? { swatchId: s.id, alpha: 1 } : { ...s.color, alpha: s.color.alpha });
            }}
            style={{
              width: 24,
              height: 24,
              padding: 0,
              background: cssColor(s.color, doc),
              border: chosen === s.id ? '2px solid var(--accent)' : '1px solid var(--swatch-border)',
              // A global swatch shows a white corner (Illustrator's mark).
              backgroundImage: s.global
                ? 'linear-gradient(225deg, var(--pure-white) 18%, transparent 19%)'
                : undefined,
            }}
          />
        ))}
      </div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <button
          data-testid="swatch-new"
          onClick={() => {
            const c = targetColor(shownAppearance(), target) ?? BLACK;
            const out: string[] = [];
            const color = c.swatchId ? (doc.swatches.find((s) => s.id === c.swatchId)?.color ?? BLACK) : c;
            useDesign
              .getState()
              .run(
                addSwatch(
                  { name: t('swatchN', { n: doc.swatches.length + 1 }), color: { ...color }, global },
                  out,
                ),
              );
            setChosen(out[0] ?? null);
          }}
        >
          {t('newSwatch')}
        </button>
        <label>
          <input
            type="checkbox"
            data-testid="swatch-global"
            checked={global}
            onChange={(e) => setGlobal(e.target.checked)}
          />{' '}
          {t('globalSwatch')}
        </label>
      </div>
      {sw && (
        <div style={{ display: 'grid', gap: 4, borderTop: '1px solid var(--line)', paddingTop: 6 }}>
          {rename === sw.id ? (
            <input
              autoFocus
              defaultValue={sw.name}
              aria-label={t('swatchName')}
              onBlur={(e) => {
                setRename(null);
                const v = e.target.value.trim();
                if (v && v !== sw.name)
                  useDesign.getState().run(updateSwatch(sw.id, (s) => void (s.name = v)));
              }}
              onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
            />
          ) : (
            <strong onDoubleClick={() => setRename(sw.id)}>
              {sw.name} {sw.global ? `· ${t('globalSwatch')}` : ''}
            </strong>
          )}
          <ColorPicker
            testid="swatch-picker"
            color={sw.color}
            cmyk={doc.colorMode === 'cmyk'}
            onChange={(c, final) =>
              useDesign
                .getState()
                .run(
                  updateSwatch(
                    sw.id,
                    (s) => void (s.color = { ...c }),
                    final ? undefined : `swatch-${sw.id}`,
                  ),
                )
            }
          />
          <button
            data-testid="swatch-delete"
            onClick={() => {
              useDesign.getState().run(removeSwatch(sw.id));
              setChosen(null);
            }}
          >
            {t('deleteSwatch')}
          </button>
        </div>
      )}
    </div>
  );
}
